import type { ApiPricePlan } from '#pricing/types/price-plan';
import type { Trait as PricePlanTrait } from './price-plan';
import type { PricePlansContainerBridgeProps } from './price-plans-container.bridge.tsx';

interface PriceCta {
  text: string;
  onPress: () => void;
}

export interface SharedAddon {
  description: string;
  iconLightPath: string;
  iconDarkPath: string;
}

interface MapPricePlanPropsOptions {
  isCompanyCreation?: boolean;
  recurrence: 'monthly' | 'annual';
  mainButton: {
    text: string;
    onPress: (code: string) => void;
  };
  isSubmitting: boolean;
  selectedPlan: string | null;
  formatPrice: (amount: number) => string;
  descriptionCta?: { type: 'button'; onPress: () => void };
  addOnsTitle: string;
  country: string;
  promoOfferBadge: string;
  registrationFlow?: 'de_full_company_creation_companies';
  hidePromoInAllProducts?: boolean;
  usePackValues?: boolean;
  hideOfferDetails?: boolean;
  preselectedBadgeText?: string;
  preselectedPlanCode?: string;
  priceCta?: PriceCta | ((plan: ApiPricePlan) => PriceCta | undefined);
  isCompactLayout?: boolean;
  compactCtaPrefix?: string;
  sharedAddonsTitle?: string;
  includedInAllPlans?: {
    title: string;
    benefits: string[];
  };
  comparePlansLink?: {
    text: string;
    href: string;
  };
  compactPlanFeatures?: Record<string, string[]>;
}

export const mapPricePlanProps = (
  pricePlans: ApiPricePlan[],
  options: MapPricePlanPropsOptions
): PricePlansContainerBridgeProps => {
  const isMonthly = options.recurrence === 'monthly';
  const isAnnual = options.recurrence === 'annual';

  const planHasPromoForRecurrence = (plan: ApiPricePlan): boolean => {
    if (isMonthly) {
      return Boolean(
        plan.monthly_price.promo_offer?.discounted_pack_price?.value ||
        plan.monthly_price.promo_offer?.discounted_price.value
      );
    }

    return Boolean(
      plan.annual_price.promo_offer?.discounted_pack_price?.value ||
      plan.annual_price.promo_offer?.discounted_monthly_price?.value ||
      plan.annual_price.promo_offer?.discounted_price.value
    );
  };

  const hasPromoInLineup =
    !options.hidePromoInAllProducts && pricePlans.some(planHasPromoForRecurrence);

  const allPlansHavePromo =
    !options.hidePromoInAllProducts &&
    pricePlans.length > 0 &&
    pricePlans.every(planHasPromoForRecurrence);
  const hasPreselectedInLineup = pricePlans.some(plan => {
    return options.preselectedPlanCode === plan.code;
  });
  const shouldPrioritizePreselected = !hasPromoInLineup && hasPreselectedInLineup;

  const deduplicateAddons = (): PricePlansContainerBridgeProps['sharedAddons'] => {
    if (!options.isCompactLayout) return undefined;

    const seen = new Set<string>();
    const addons: { description: string; iconLightPath: string; iconDarkPath: string }[] = [];

    for (const plan of pricePlans) {
      for (const addon of plan.add_ons) {
        if (!seen.has(addon.brand_name)) {
          seen.add(addon.brand_name);
          addons.push({
            description: addon.brand_name,
            iconLightPath: addon.icon_light_url,
            iconDarkPath: addon.icon_dark_url,
          });
        }
      }
    }

    return addons.length > 0
      ? { title: options.sharedAddonsTitle ?? options.addOnsTitle, addons }
      : undefined;
  };

  return {
    isCompactLayout: options.isCompactLayout,
    sharedAddons: deduplicateAddons(),
    includedInAllPlans: options.includedInAllPlans,
    comparePlansLink: options.comparePlansLink,
    hasPromoInAnyProducts: hasPromoInLineup,
    ...(allPlansHavePromo ? { hidePromoHighlights: true } : {}),
    pricePlans: pricePlans.map(plan => {
      const {
        brand_pack_name,
        code,
        brand_name,
        traits,
        lineup,
        monthly_price,
        annual_price,
        description,
        trial_duration,
        benefit_groups,
        top_highlighted_benefits,
        add_ons,
        capital_deposit_fee,
        offer_details,
        subtitle,
      } = plan;

      const isGermany = options.country === 'DE';

      let promoPriceValue;

      if (!options.hidePromoInAllProducts) {
        if (isMonthly) {
          promoPriceValue =
            monthly_price.promo_offer?.discounted_pack_price?.value ??
            monthly_price.promo_offer?.discounted_price.value;
        } else {
          promoPriceValue =
            annual_price.promo_offer?.discounted_pack_price?.value ??
            annual_price.promo_offer?.discounted_monthly_price?.value ??
            annual_price.promo_offer?.discounted_price.value;
        }
      }

      const promoOfferPrice = promoPriceValue
        ? options.formatPrice(parseFloat(promoPriceValue))
        : undefined;

      let traitsMap: PricePlanTrait[] = traits.map(trait => ({
        name: trait.name,
        title: trait.title,
      }));
      const isPopular = traitsMap.some(trait => trait.name === 'popular');
      const isRecommended = traitsMap.some(trait => trait.name === 'recommended');
      const isPreselected = options.preselectedPlanCode === code;

      if (promoOfferPrice) {
        traitsMap = [
          {
            name: 'promo',
            title: options.promoOfferBadge,
          },
        ];
      } else if (shouldPrioritizePreselected) {
        if (isPreselected && options.preselectedBadgeText) {
          traitsMap = [
            {
              name: 'preselected',
              title: options.preselectedBadgeText,
            },
          ];
        } else {
          traitsMap = [];
        }
      }

      const billing_recurrence = isMonthly
        ? monthly_price.billing_recurrence
        : annual_price.billing_recurrence;

      const annual_benefit = isMonthly ? monthly_price.annual_benefit : annual_price.annual_benefit;

      const getTitle = (): string => {
        if (options.isCompanyCreation && isAnnual && !isGermany) {
          return brand_pack_name;
        }

        if (options.isCompanyCreation && isMonthly && isGermany) {
          return brand_pack_name;
        }

        return brand_name;
      };

      const getPriceDescription = (): string | undefined => {
        if (promoOfferPrice) {
          return billing_recurrence;
        }

        if (options.isCompanyCreation) {
          return isAnnual ? annual_benefit : capital_deposit_fee;
        }

        if (isAnnual) {
          return billing_recurrence;
        }
      };

      const getCtaDescription = (): string => {
        if (promoOfferPrice) {
          return trial_duration || '';
        }
        if (options.isCompanyCreation) {
          return trial_duration || billing_recurrence;
        }
        return trial_duration || '';
      };

      const getPrice = (): string => {
        if (options.usePackValues) {
          if (monthly_price.pack_amount) {
            return options.formatPrice(parseFloat(monthly_price.pack_amount.value));
          }
        }

        if (options.isCompanyCreation && !isGermany) {
          if (isAnnual) {
            return options.formatPrice(
              parseFloat(annual_price.pack_amount?.value || annual_price.per_year_amount.value)
            );
          }
          return options.formatPrice(parseFloat(monthly_price.per_month_amount.value));
        }

        if (isAnnual) {
          return options.formatPrice(parseFloat(annual_price.per_month_amount.value));
        }

        return options.formatPrice(parseFloat(monthly_price.per_month_amount.value));
      };

      const getPricePeriod = (): string => {
        if (options.isCompanyCreation && isAnnual && !isGermany) {
          return annual_price.period;
        }

        return monthly_price.period;
      };

      const getStrikeThroughPrice = (): string | undefined => {
        if (options.usePackValues) {
          if (annual_price.pack_amount_before_discount?.value) {
            return options.formatPrice(parseFloat(annual_price.pack_amount_before_discount.value));
          }
          if (monthly_price.pack_amount_before_discount?.value) {
            return options.formatPrice(parseFloat(monthly_price.pack_amount_before_discount.value));
          }

          return undefined;
        }

        if (isMonthly || (options.isCompanyCreation && !isGermany)) {
          return undefined;
        }

        if (!monthly_price.per_month_amount.value) {
          return undefined;
        }

        return options.formatPrice(parseFloat(monthly_price.per_month_amount.value));
      };

      const getSubDescription = (): string | undefined => {
        const isPack = Boolean(
          (isAnnual && annual_price.pack_amount) || (isMonthly && monthly_price.pack_amount)
        );
        if (isPack) {
          return '';
        }
        if (isMonthly) {
          return monthly_price.sub_description;
        }

        return annual_price.sub_description;
      };

      const staticFeatures = options.compactPlanFeatures?.[brand_name.toLowerCase()];

      function getTopHighlightedBenefits() {
        if (staticFeatures) {
          return {
            title: '',
            benefits: staticFeatures.map(feature => ({
              description: feature,
              isIncluded: true,
            })),
          };
        }

        if (top_highlighted_benefits) {
          return {
            title: top_highlighted_benefits.title,
            benefits: top_highlighted_benefits.benefits.map(benefit => ({
              description: benefit.description,
              isIncluded: benefit.is_included,
            })),
          };
        }

        return undefined;
      }

      const topHighlightedBenefits = getTopHighlightedBenefits();

      const base = {
        title: getTitle(),
        subtitle,
        traits: traitsMap,
        price: getPrice(),
        monthlyPrice: getStrikeThroughPrice(),
        pricePeriod: getPricePeriod(),
        description,
        lineup,
        priceDescription: getPriceDescription(),
        ctaDescription: options.isCompactLayout ? '' : getCtaDescription(),
        ...(options.hideOfferDetails ? {} : { offerDetails: offer_details }),
        subDescription: getSubDescription(),
        mainButton: {
          text:
            options.isCompactLayout && getCtaDescription()
              ? `${options.compactCtaPrefix ?? ''} ${getCtaDescription()}`.trim()
              : options.mainButton.text,
          buttonProps: {
            variant: (() => {
              if (shouldPrioritizePreselected) {
                return isPreselected ? ('primary' as const) : ('secondary' as const);
              }

              return isPopular || isRecommended ? ('primary' as const) : ('secondary' as const);
            })(),
            onPress: () => {
              options.mainButton.onPress(code);
            },
            isLoading: options.isSubmitting && options.selectedPlan === code,
            isDisabled: options.isSubmitting && options.selectedPlan !== code,
          },
        },
        compact: options.isCompactLayout ?? false,
        topHighlightedBenefits,
        benefitsGroups: benefit_groups.map(benefitGroup => ({
          title: benefitGroup.title,
          benefits: benefitGroup.benefits.map(benefit => ({
            description: benefit.description,
            isIncluded: benefit.is_included,
          })),
        })),
        addons:
          !options.isCompactLayout && add_ons.length > 0
            ? {
                title: options.addOnsTitle,
                addons: add_ons.map(addon => ({
                  description: addon.brand_name,
                  iconLightPath: addon.icon_light_url,
                  iconDarkPath: addon.icon_dark_url,
                })),
              }
            : null,
        promoOfferPrice,
      };

      if (isMonthly) {
        if (options.priceCta !== undefined) {
          return {
            ...base,
            recurrence: 'monthly' as const,
            priceCta:
              typeof options.priceCta === 'function' ? options.priceCta(plan) : options.priceCta,
          };
        }

        return {
          ...base,
          recurrence: 'monthly' as const,
          ...(options.descriptionCta && {
            descriptionCta: {
              text: monthly_price.annual_benefit,
              onPress: options.descriptionCta.onPress,
            },
          }),
        };
      }

      // recurrence === 'annual'
      return {
        ...base,
        recurrence: 'annual' as const,
      };
    }),
  };
};
